import { useEffect, useRef, useState } from "react";
import { copyText } from "../lib/clipboard";
import { useAnnounce } from "./announcer-context";
import { useToast } from "./toast-context";

/**
 * Copies exactly, shows a confirmation for `durationMs` and announces it once through the polite live region.
 * GRF-27: with `toast`, the confirmation also shows as a toast (for a control that closes or has no room beside it).
 */
export function useCopy(durationMs = 2000, options: { toast?: boolean } = {}) {
  const announce = useAnnounce();
  const showToast = useToast();
  const toast = options.toast === true;
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  async function copy(value: string, successMessage = `Copied ${value}`): Promise<boolean> {
    const copied = await copyText(value);
    const message = copied ? successMessage : `Could not copy ${value}. Select it and copy it manually.`;
    setConfirmation(message);
    announce(message);
    if (toast) showToast(message);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setConfirmation(null), durationMs);
    return copied;
  }

  return { copy, confirmation };
}
